import { Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import { LocationInventory } from "../../../../features/FloorManager/typings/location-inventory";
import { useRemoteVisitOnlyVisitActions } from "../../../../hooks/Remote/Schedule/useRemoteVisitOnlyVisitActions";
import { isVisitorScheduleFieldEditable } from "../../functions/visitor-schedule-field-editable.function";
import { ScheduleSelectionVisitorLocationField } from "./schedule-selection-visitor-location-field.component";
import {
  buildVisitorInfoPatchPayload,
  canEditVisitorScheduleDetails,
  formStateToEditDraft,
  hasVisitorDisplayData,
  resolveScheduleVisitors,
  ScheduleVisitorDisplayEntry,
  ScheduleVisitorEditFormState,
  visitorEditFormFromEntry
} from "./schedule-selection-visitor-edit.logic";

type P = {
  schedule: BookingScheduleInterface;
  isDialogEditable: boolean;
  isColleagueScheduleEditable: boolean;
  locations?: LocationInventory[];
  onSaved?: () => void;
};

const ResponsiveInfoRow = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      justifyContent: "space-between",
      alignItems: { xs: "stretch", md: "flex-start" },
      gap: { xs: 0.25, md: 2 }
    }}
  >
    <Typography
      color="text.secondary"
      sx={{
        flexShrink: 0,
        whiteSpace: "nowrap",
        fontSize: { xs: "0.78rem", md: "1rem" },
        fontWeight: { xs: 400, md: 400 },
        letterSpacing: { xs: "0.02em", md: "normal" }
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      fontWeight={{ xs: 600, md: 500 }}
      sx={{
        width: { xs: "100%", md: "auto" },
        flex: { md: 1 },
        minWidth: 0,
        textAlign: { xs: "left", md: "right" },
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere"
      }}
    >
      {value}
    </Typography>
  </Box>
);

type VisitorSectionProps = {
  index: number;
  visitor: ScheduleVisitorDisplayEntry;
  schedule: BookingScheduleInterface;
  canEdit: boolean;
  isSaving: boolean;
  onSave: (index: number, form: ScheduleVisitorEditFormState) => Promise<void>;
};

function ScheduleSelectionVisitorSection({
  index,
  visitor,
  schedule,
  canEdit,
  isSaving,
  onSave
}: Readonly<VisitorSectionProps>) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<ScheduleVisitorEditFormState>(() =>
    visitorEditFormFromEntry(visitor, schedule)
  );

  const canEditGuestName = isVisitorScheduleFieldEditable(schedule, "guestName");
  const canEditGuestOther = isVisitorScheduleFieldEditable(schedule, "guestOther");
  const canEditVisitReason = isVisitorScheduleFieldEditable(schedule, "visitReason");
  const canEditLocation = isVisitorScheduleFieldEditable(schedule, "location");
  const showEditControl =
    canEdit && (canEditGuestName || canEditGuestOther || canEditVisitReason || canEditLocation);

  const patchForm = useCallback((patch: Partial<ScheduleVisitorEditFormState>) => {
    setForm(prev => ({ ...prev, ...patch }));
  }, []);

  const handleStartEdit = () => {
    setForm(visitorEditFormFromEntry(visitor, schedule));
    setIsEditing(true);
  };

  const handleCancel = () => {
    setForm(visitorEditFormFromEntry(visitor, schedule));
    setIsEditing(false);
  };

  const handleSave = async () => {
    await onSave(index, form);
    setIsEditing(false);
  };

  const visitReasonTrimmed = String(
    visitor.visitReason ?? schedule.externalVisitReason ?? ""
  ).trim();
  const locationTrimmed = String(visitor.locationName ?? schedule.locationName ?? "").trim();
  const visitorKey = `${visitor.visitorRegistrationId ?? visitor.email ?? "visitor"}-${index}`;

  return (
    <Stack spacing={1} data-testid={`schedule-selection-visitor-section-${index}`}>
      <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t("_visitorBooking_visitorLabel", { n: index + 1 })}
        </Typography>
        {showEditControl && !isEditing && (
          <Tooltip title={t("Edit")}>
            <IconButton
              aria-label={t("Edit")}
              data-testid={`schedule-selection-visitor-edit-btn-${index}`}
              disabled={isSaving}
              onClick={handleStartEdit}
              size="small"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {isEditing ? (
        <Stack spacing={2} data-testid={`schedule-selection-visitor-edit-form-${index}`}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              required
              fullWidth
              disabled={!canEditGuestName}
              label={t("_visitorBooking_firstName")}
              value={form.firstName}
              onChange={e => patchForm({ firstName: e.target.value })}
              data-testid={`schedule-selection-visitor-first-name-${index}`}
            />
            <TextField
              required
              fullWidth
              disabled={!canEditGuestName}
              label={t("_visitorBooking_surname")}
              value={form.surname}
              onChange={e => patchForm({ surname: e.target.value })}
              data-testid={`schedule-selection-visitor-surname-${index}`}
            />
          </Stack>
          <TextField
            fullWidth
            disabled={!canEditGuestOther}
            label={t("Company")}
            value={form.company}
            onChange={e => patchForm({ company: e.target.value })}
            data-testid={`schedule-selection-visitor-company-${index}`}
          />
          <TextField
            required
            fullWidth
            type="email"
            disabled={!canEditGuestOther}
            label={t("_visitorBooking_email")}
            value={form.email}
            onChange={e => patchForm({ email: e.target.value })}
            data-testid={`schedule-selection-visitor-email-${index}`}
          />
          <TextField
            fullWidth
            type="tel"
            disabled={!canEditGuestOther}
            label={t("_visitorBooking_contacts")}
            value={form.contacts}
            onChange={e => patchForm({ contacts: e.target.value })}
            data-testid={`schedule-selection-visitor-contacts-${index}`}
          />
          <TextField
            required
            fullWidth
            multiline
            maxRows={3}
            disabled={!canEditVisitReason}
            label={t("_visitorBooking_visitReason")}
            value={form.visitReason}
            onChange={e => patchForm({ visitReason: e.target.value })}
            data-testid={`schedule-selection-visitor-visit-reason-${index}`}
          />
          {canEditLocation ? (
            <ScheduleSelectionVisitorLocationField
              value={form.locationName}
              onChange={locationName => patchForm({ locationName })}
            />
          ) : (
            <TextField
              fullWidth
              disabled
              label={t("Location")}
              value={form.locationName}
              data-testid={`schedule-selection-visitor-location-readonly-${index}`}
            />
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              color="secondary"
              disabled={isSaving}
              onClick={handleCancel}
              data-testid={`schedule-selection-visitor-cancel-btn-${index}`}
            >
              {t("Cancel")}
            </Button>
            <Button
              disabled={isSaving}
              onClick={() => {
                handleSave().catch(() => undefined);
              }}
              data-testid={`schedule-selection-visitor-save-btn-${index}`}
            >
              {t("Save")}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <>
          {visitor.firstName?.trim() && (
            <ResponsiveInfoRow
              label={t("_visitorBooking_firstName")}
              value={visitor.firstName.trim()}
            />
          )}
          {visitor.surname?.trim() && (
            <ResponsiveInfoRow
              label={t("_visitorBooking_surname")}
              value={visitor.surname.trim()}
            />
          )}
          {locationTrimmed !== "" && (
            <ResponsiveInfoRow label={t("Location")} value={locationTrimmed} />
          )}
          {visitor.company?.trim() && (
            <ResponsiveInfoRow label={t("Company")} value={visitor.company.trim()} />
          )}
          {visitor.email?.trim() && (
            <ResponsiveInfoRow label={t("_visitorBooking_email")} value={visitor.email.trim()} />
          )}
          {visitor.contacts?.trim() && (
            <ResponsiveInfoRow
              label={t("_visitorBooking_contacts")}
              value={visitor.contacts.trim()}
            />
          )}
          <ResponsiveInfoRow
            label={t("_visitorBooking_visitReason")}
            value={visitReasonTrimmed !== "" ? visitReasonTrimmed : t("Not set")}
          />
        </>
      )}

      <Box sx={{ display: "none" }} data-testid={`schedule-selection-visitor-key-${visitorKey}`} />
    </Stack>
  );
}

/**
 * Visitor details for workplace visit-only and bookPlace bookings in the schedule selection dialog.
 * Hosts can inline-edit visitor fields (same rules as visitor admin) via the pencil control.
 */
export function ScheduleSelectionVisitorCardContents({
  schedule,
  isDialogEditable,
  isColleagueScheduleEditable,
  locations,
  onSaved
}: Readonly<P>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    userInformation: { sub: userId },
    settings: { timezone }
  } = useSelector((state: RootState) => state.login);

  const { patchVisitOnly, patchConference } = useRemoteVisitOnlyVisitActions();
  const isSaving = patchVisitOnly.isLoading || patchConference.isLoading;

  const canEdit = canEditVisitorScheduleDetails({
    schedule,
    userId,
    isDialogEditable,
    isColleagueScheduleEditable
  });

  const visitorsWithData = resolveScheduleVisitors(schedule).filter(hasVisitorDisplayData);

  const handleSave = useCallback(
    async (index: number, form: ScheduleVisitorEditFormState) => {
      const visitor = visitorsWithData[index];
      if (!visitor) {
        return;
      }

      const draft = formStateToEditDraft(form);
      const payload = buildVisitorInfoPatchPayload({
        schedule,
        visitor,
        draft,
        locations,
        timezone: schedule.timezone ?? timezone
      });

      if (!payload) {
        enqueueSnackbar(t("_visitorBookings_editNothingToSave"), { variant: "info" });
        return;
      }

      try {
        if (payload.kind === "conference") {
          await patchConference.mutateAsync({
            visitorRegistrationId: payload.visitorRegistrationId,
            body: payload.body
          });
        } else {
          await patchVisitOnly.mutateAsync({
            visitId: schedule.id,
            body: payload.body
          });
        }
        enqueueSnackbar(t("_visitorBookings_editSavedSuccess"), { variant: "success" });
        onSaved?.();
      } catch {
        enqueueSnackbar(t("_visitorBookings_editFailed"), { variant: "error" });
      }
    },
    [
      visitorsWithData,
      schedule,
      locations,
      timezone,
      enqueueSnackbar,
      t,
      patchConference,
      patchVisitOnly,
      onSaved
    ]
  );

  if (visitorsWithData.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="schedule-selection-visitor-empty"
      >
        {t("Not set")}
      </Typography>
    );
  }

  return (
    <Card
      data-testid="schedule-selection-visitor-card"
      elevation={0}
      sx={{
        borderRadius: 2,
        border: theme => `1px solid ${theme.palette.divider}`,
        maxHeight: "65vh",
        overflowY: "auto"
      }}
    >
      <CardContent sx={{ pb: 0, "&:last-child": { pb: 2 } }}>
        <Stack spacing={2}>
          {visitorsWithData.map((visitor, index) => (
            <Stack
              key={`${visitor.visitorRegistrationId ?? visitor.email ?? "visitor"}-${index}`}
              spacing={1}
            >
              <ScheduleSelectionVisitorSection
                index={index}
                visitor={visitor}
                schedule={schedule}
                canEdit={canEdit}
                isSaving={isSaving}
                onSave={handleSave}
              />
              {index < visitorsWithData.length - 1 && <Divider sx={{ opacity: 0.6 }} />}
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
